import { useEffect, useRef, useState } from 'react'

const POLL_INTERVAL = 5 * 60 * 1000 // 5 minutes
const IDLE_RELOAD_DELAY = 30 * 1000  // 30s after detecting update, reload if idle

// Polls /api/version every 5 minutes. When the deployment ID changes:
//  - If the user hasn't interacted in 30s, reloads silently.
//  - Otherwise shows a banner so they can choose when to update.
export default function useDeploymentRefresh() {
  const [updateReady, setUpdateReady] = useState(false)
  const knownVersion = useRef(null)
  const lastActivity = useRef(Date.now())
  const idleTimer = useRef(null)

  // Track last user interaction
  useEffect(() => {
    const touch = () => { lastActivity.current = Date.now() }
    window.addEventListener('mousemove', touch, { passive: true })
    window.addEventListener('keydown',   touch, { passive: true })
    window.addEventListener('click',     touch, { passive: true })
    return () => {
      window.removeEventListener('mousemove', touch)
      window.removeEventListener('keydown',   touch)
      window.removeEventListener('click',     touch)
    }
  }, [])

  useEffect(() => {
    async function check() {
      try {
        const res = await fetch('/api/version', { cache: 'no-store' })
        if (!res.ok) return
        const { version } = await res.json()
        if (!version || version === 'local') return

        if (!knownVersion.current) {
          knownVersion.current = version
          return
        }

        if (version !== knownVersion.current) {
          const idleSecs = (Date.now() - lastActivity.current) / 1000
          if (idleSecs >= 30) {
            window.location.reload()
          } else {
            setUpdateReady(true)
            // Auto-reload after 30s of idle, even if they were active when we detected the update
            clearTimeout(idleTimer.current)
            idleTimer.current = setTimeout(() => {
              if ((Date.now() - lastActivity.current) >= 30000) {
                window.location.reload()
              }
            }, IDLE_RELOAD_DELAY)
          }
        }
      } catch {
        // network unavailable — skip silently
      }
    }

    check() // immediate check on mount
    const id = setInterval(check, POLL_INTERVAL)
    return () => { clearInterval(id); clearTimeout(idleTimer.current) }
  }, [])

  const applyUpdate = () => window.location.reload()

  return { updateReady, applyUpdate }
}
